<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useCrm } from 'dashboard/composables/useCrm';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import CrmStageSettings from './CrmStageSettings.vue';
import CrmNextStagePreview from './CrmNextStagePreview.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  funnelId: { type: [String, Number], default: '' },
  requestedStage: { type: String, default: '' },
});
const emit = defineEmits(['changed', 'moveHandled']);
const { t } = useI18n();
const store = useStore();
const contactLabels = useMapGetter('contactLabels/getContactLabels');
const id = computed(() => props.conversationId);
const { state, active, load, loadOlder, act } = useCrm(id);
const data = computed(() => state.value.data);
const conversationClosed = computed(
  () => data.value?.conversation_status === 'resolved'
);
const route = useRoute();
const chosen = ref(String(props.funnelId || route.query.crm_funnel || ''));
const modal = ref(null);
const dialog = ref('');
const reason = ref('');
const note = ref('');
const minutes = ref(60);
const target = ref('');
const confirmPending = ref(false);
const confirmReview = ref(false);
const showSettings = ref(false);
const showHistory = ref(false);
const messageId = ref('');
const undoId = ref(null);
const milestoneBusy = ref(false);
const milestoneError = ref('');
const funnel = computed(() =>
  data.value?.funnels.find(
    f => f.id === (active.value?.funnel_id || Number(chosen.value))
  )
);
const record = computed(
  () =>
    active.value ||
    data.value?.records.find(r => r.funnel_id === funnel.value?.id)
);
const columns = computed(
  () => record.value?.state.stages || funnel.value?.columns || []
);
const stage = computed(() =>
  columns.value.find(s => s.id === record.value?.column_id)
);
const completed = computed(
  () => record.value?.state.completed?.[record.value.column_id] || {}
);
const requirements = computed(() => stage.value?.requirements || []);
const classifications = computed(() => {
  const current = funnel.value?.columns.find(
    item => item.id === stage.value?.id
  );
  return stage.value?.classifications?.length
    ? stage.value.classifications
    : current?.classifications || [];
});
const selectedClassifications = computed(
  () => record.value?.state.classifications || {}
);
const classificationsComplete = computed(() =>
  classifications.value.every(
    item => selectedClassifications.value[item.id]?.value
  )
);
const milestoneLabels = computed(() => stage.value?.milestone_labels || []);
const selectedMilestones = computed(() =>
  contactLabels.value(data.value?.contact_id)
);
const completeCount = computed(
  () => requirements.value.filter(r => completed.value[r.id]).length
);
const previousStages = computed(
  () => columns.value.slice(0, columns.value.indexOf(stage.value)) || []
);
const nextStage = computed(
  () => columns.value[columns.value.indexOf(stage.value) + 1]
);
const nextStageActions = computed(() =>
  data.value?.next_stage_actions?.stage_id === nextStage.value?.id
    ? data.value.next_stage_actions
    : null
);
const editable = computed(
  () =>
    data.value?.can_edit &&
    active.value?.conversation_id === props.conversationId
);
const format = value =>
  value ? new Date(value).toLocaleString() : t('CRM.noDeadline');
const openDialog = value => {
  dialog.value = value;
  reason.value = '';
  note.value = '';
  confirmPending.value = false;
  confirmReview.value = false;
};
watch(
  id,
  () => {
    dialog.value = '';
    showSettings.value = false;
    showHistory.value = false;
    load();
  },
  { immediate: true }
);
watch(
  data,
  value => {
    if (!chosen.value && value?.funnels.length)
      chosen.value = String(value.funnels[0].id);
  },
  { immediate: true }
);
watch(
  () => data.value?.contact_id,
  contactId => {
    if (contactId) store.dispatch('contactLabels/get', contactId);
  },
  { immediate: true }
);
const chooseMilestone = async title => {
  const contactId = data.value?.contact_id;
  if (!contactId || !editable.value || milestoneBusy.value) return;

  milestoneBusy.value = true;
  milestoneError.value = '';
  try {
    const current = (await ContactAPI.getContactLabels(contactId)).data.payload;
    const labels =
      stage.value?.milestone_selection === 'single'
        ? current.filter(item => !milestoneLabels.value.includes(item))
        : current.filter(item => item !== title);
    if (!current.includes(title)) labels.push(title);
    await store.dispatch('contactLabels/update', { contactId, labels });
  } catch {
    milestoneError.value = t('CRM.milestones.saveError');
  } finally {
    milestoneBusy.value = false;
  }
};
watch(
  () => state.value.closing,
  value => {
    if (value) {
      openDialog('close');
      state.value.closing = false;
    }
  },
  { immediate: true }
);
const execute = async (operation, extra = {}) => {
  const current = record.value || { funnel_id: funnel.value.id, version: 0 };
  if (operation === 'start' && conversationClosed.value) {
    state.value.busy = true;
    await store.dispatch('toggleStatus', {
      conversationId: props.conversationId,
      status: 'open',
    });
    await load();
    if (conversationClosed.value) {
      state.value.error = t('CONVERSATION.CHANGE_STATUS_FAILED');
      return;
    }
  }
  if (await act(operation, current, extra)) {
    dialog.value = '';
    emit('changed');
  }
};
const chooseClassification = (classification, value) =>
  execute('classify', { classification_id: classification.id, value });
watch(
  dialog,
  async value => {
    if (value) {
      await nextTick();
      modal.value?.showModal();
    }
  },
  { immediate: true }
);
watch([data, () => props.requestedStage, () => state.value.busy], () => {
  if (!props.requestedStage || !data.value || state.value.busy) return;
  const destination = props.requestedStage;
  emit('moveHandled');
  if (!editable.value) {
    state.value.error = t('CRM.readOnly');
    return;
  }
  const from = columns.value.indexOf(stage.value);
  const to = columns.value.findIndex(column => column.id === destination);
  if (to === from) return;
  if (to < 0 || to > from + 1) {
    state.value.error = t('CRM.board.linearOnly');
    return;
  }
  target.value = destination;
  if (to < from) {
    openDialog('return');
  } else if (
    (completeCount.value < requirements.value.length &&
      !data.value.skip_confirmation) ||
    active.value.state.review_required
  ) {
    openDialog('advance');
  } else {
    execute('advance');
  }
});
const auditSummary = snapshot => {
  if (snapshot.columns)
    return snapshot.columns
      .map(
        s => `${s.name}: ${s.requirements?.map(r => r.name).join(', ') || '—'}`
      )
      .join(' · ');
  if ('skip_confirmation' in snapshot)
    return `${t('CRM.skipConfirmation')}: ${snapshot.skip_confirmation}`;
  const workflow = snapshot.workflow || {};
  const name =
    workflow.stages?.find(s => s.id === snapshot.column_id)?.name ||
    snapshot.column_id;
  const count = Object.values(workflow.completed || {}).reduce(
    (sum, stageItems) => sum + Object.keys(stageItems).length,
    0
  );
  return [
    name,
    workflow.status ? t(`CRM.${workflow.status}`) : '',
    t('CRM.completedCount', { count }),
    format(snapshot.deadline_at),
  ]
    .filter(Boolean)
    .join(' · ');
};
const submit = () =>
  execute(dialog.value, {
    reason: reason.value,
    note: note.value,
    minutes: minutes.value,
    column_id: target.value,
    confirm_pending: confirmPending.value,
    confirm_review: confirmReview.value,
    message_id: messageId.value,
    event_id: undoId.value,
  });
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-2 p-4 text-sm text-n-slate-12">
    <p
      v-if="state.error"
      role="alert"
      class="rounded-lg bg-n-ruby-3 p-3 text-n-ruby-11"
    >
      {{ state.error }}
    </p>
    <button
      v-if="!data"
      class="rounded-lg border border-n-weak p-2"
      @click="load"
    >
      {{ $t('CRM.retry') }}
    </button>
    <template v-else>
      <CrmStageSettings
        v-if="showSettings && funnel"
        :funnel="funnel"
        :initial-stage-id="record?.column_id || ''"
        @cancel="showSettings = false"
        @saved="
          showSettings = false;
          load();
        "
      />
      <template v-else>
        <template v-if="!showHistory">
          <ComboBox
            v-if="!active"
            v-model="chosen"
            :allow-deselect="false"
            :options="
              data.funnels.map(item => ({
                value: String(item.id),
                label: item.name,
              }))
            "
            :aria-label="$t('CRM.flow')"
            class="mb-3"
          />
          <div v-if="funnel" class="flex min-w-0 items-center gap-2">
            <span
              class="i-lucide-history size-5 shrink-0 text-n-slate-11"
              aria-hidden="true"
            />
            <h3 class="m-0 min-w-0 flex-1 break-words text-base font-semibold">
              {{ stage?.name || funnel.name }}
            </h3>
            <details class="group relative shrink-0">
              <summary
                class="flex size-9 cursor-pointer list-none items-center justify-center rounded-lg hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand [&::-webkit-details-marker]:hidden"
                :aria-label="$t('CRM.moreOptions')"
              >
                <span
                  class="i-lucide-ellipsis-vertical size-5"
                  aria-hidden="true"
                />
              </summary>
              <div
                class="absolute right-0 top-10 z-10 min-w-40 rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
              >
                <button
                  type="button"
                  class="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-n-alpha-2"
                  @click="showHistory = true"
                >
                  <span class="i-lucide-history size-4" aria-hidden="true" />
                  {{ $t('CRM.history') }}
                </button>
                <button
                  v-if="data.administrator"
                  type="button"
                  class="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-n-alpha-2"
                  @click="showSettings = true"
                >
                  <span class="i-lucide-settings size-4" aria-hidden="true" />
                  {{ $t('CRM.configure') }}
                </button>
              </div>
            </details>
          </div>

          <template v-if="active">
            <div
              class="mb-3 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
            >
              <span
                class="inline-flex min-w-0 max-w-full items-center gap-1.5"
                :title="
                  [
                    $t('CRM.responsible'),
                    active.assignee || $t('CRM.unassigned'),
                    active.team,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                "
              >
                <span
                  class="i-lucide-user-round size-4 shrink-0"
                  aria-hidden="true"
                />
                <span class="truncate">{{
                  active.assignee || $t('CRM.unassignedShort')
                }}</span>
                <template v-if="active.team"
                  ><span class="truncate">· {{ active.team }}</span></template
                >
              </span>
              <component
                :is="editable && active.deadline_at ? 'button' : 'span'"
                class="inline-flex min-w-0 items-center gap-1.5 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :class="{
                  'text-n-ruby-11':
                    active.deadline_at &&
                    new Date(active.deadline_at) < new Date(),
                  'hover:text-n-blue-11': editable && active.deadline_at,
                }"
                :title="`${$t('CRM.deadline')}: ${format(active.deadline_at)}`"
                :aria-label="`${$t('CRM.deadline')}: ${format(active.deadline_at)}${editable && active.deadline_at ? ` · ${$t('CRM.extend')}` : ''}`"
                :disabled="
                  editable && active.deadline_at ? state.busy : undefined
                "
                @click="editable && active.deadline_at && openDialog('extend')"
              >
                <span
                  class="i-lucide-clock size-4 shrink-0"
                  aria-hidden="true"
                />
                <span>{{
                  active.deadline_at
                    ? new Date(active.deadline_at).toLocaleString(undefined, {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : $t('CRM.noDeadlineShort')
                }}</span>
                <span
                  v-if="editable && active.deadline_at"
                  class="i-lucide-pencil size-3 shrink-0"
                  aria-hidden="true"
                />
              </component>
            </div>
            <p
              v-if="active.state.review_required"
              class="rounded-lg bg-n-amber-3 p-3 text-n-amber-11"
            >
              {{ $t('CRM.reviewPreserved') }}
            </p>
            <section
              v-if="classifications.length"
              class="rounded-lg border border-n-weak p-3"
              :aria-label="$t('CRM.classifications.title')"
            >
              <h4 class="m-0 text-sm font-semibold">
                {{ $t('CRM.classifications.title') }}
              </h4>
              <div
                v-for="classification in classifications"
                :key="classification.id"
                class="mt-3"
              >
                <p class="mb-2 mt-0 text-xs text-n-slate-11">
                  {{ classification.name }}
                </p>
                <div
                  class="flex flex-wrap gap-2"
                  role="group"
                  :aria-label="classification.name"
                >
                  <button
                    v-for="option in classification.options"
                    :key="option"
                    type="button"
                    class="min-h-10 rounded-lg border px-3 py-2 text-sm disabled:opacity-50"
                    :class="
                      selectedClassifications[classification.id]?.value ===
                      option
                        ? 'border-n-blue-9 bg-n-blue-3 text-n-blue-11'
                        : 'border-n-weak bg-n-solid-2 text-n-slate-12'
                    "
                    :aria-pressed="
                      selectedClassifications[classification.id]?.value ===
                      option
                    "
                    :disabled="!editable || state.busy"
                    @click="chooseClassification(classification, option)"
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
            </section>
            <details
              :key="`${id}:${active.column_id}`"
              class="group rounded-lg border border-n-weak"
            >
              <summary
                class="flex min-h-11 cursor-pointer list-none items-center gap-2 px-3 py-2 font-medium [&::-webkit-details-marker]:hidden"
              >
                <span
                  class="i-lucide-file-text size-4 shrink-0 text-n-slate-11"
                  aria-hidden="true"
                />
                <span class="flex-1">{{ $t('CRM.progressItems.title') }}</span>
                <span class="text-xs font-normal text-n-slate-11">{{
                  requirements.length || milestoneLabels.length
                    ? `${completeCount + milestoneLabels.filter(label => selectedMilestones.includes(label)).length}/${requirements.length + milestoneLabels.length}`
                    : $t('CRM.none')
                }}</span>
                <span
                  class="i-lucide-plus size-4 shrink-0 text-n-blue-11 group-open:hidden"
                  aria-hidden="true"
                />
                <span
                  class="i-lucide-minus hidden size-4 shrink-0 text-n-blue-11 group-open:block"
                  aria-hidden="true"
                />
              </summary>
              <div class="flex flex-col gap-3 px-3 pb-3 pt-1">
                <label
                  v-for="item in requirements"
                  :key="item.id"
                  class="flex items-start gap-2"
                >
                  <input
                    type="checkbox"
                    :checked="!!completed[item.id]"
                    :disabled="state.busy || !editable"
                    @change="
                      execute('requirement', {
                        requirement_id: item.id,
                        completed: $event.target.checked,
                      })
                    "
                  />
                  <span class="min-w-0 flex-1 break-words">{{
                    item.name
                  }}</span>
                  <span class="rounded bg-n-alpha-2 px-2 text-xs">{{
                    $t(`CRM.${item.owner}`)
                  }}</span>
                </label>
                <Button
                  v-if="requirements.length"
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-check-check"
                  class="bg-n-solid-3"
                  :label="$t('CRM.completeAll')"
                  :disabled="state.busy || !editable"
                  @click="execute('all')"
                />
                <p
                  v-if="milestoneLabels.length"
                  class="m-0 border-t border-n-weak pt-2 text-xs text-n-slate-11"
                >
                  {{ $t('CRM.milestones.help') }}
                </p>
                <button
                  v-for="label in milestoneLabels"
                  :key="label"
                  type="button"
                  class="min-h-10 rounded-lg border px-3 py-2 text-left text-sm disabled:opacity-50"
                  :class="
                    selectedMilestones.includes(label)
                      ? 'border-n-blue-9 bg-n-blue-3 text-n-blue-11'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-12'
                  "
                  :aria-pressed="selectedMilestones.includes(label)"
                  :disabled="!editable || milestoneBusy || state.busy"
                  @click="chooseMilestone(label)"
                >
                  {{ label }}
                </button>
                <p v-if="milestoneError" class="m-0 text-xs text-n-ruby-11">
                  {{ milestoneError }}
                </p>
              </div>
            </details>
            <details class="group rounded-lg border border-n-weak">
              <summary
                class="flex min-h-11 cursor-pointer list-none items-center gap-2 px-3 py-2 font-medium [&::-webkit-details-marker]:hidden"
              >
                <span
                  class="i-lucide-refresh-cw size-4 shrink-0 text-n-slate-11"
                  aria-hidden="true"
                />
                <span class="flex-1">{{ $t('CRM.followUp') }}</span>
                <span class="text-xs font-normal text-n-slate-11">{{
                  $t('CRM.attemptCount', {
                    count: active.state.follow_ups?.length || 0,
                  })
                }}</span>
                <span
                  class="i-lucide-plus size-4 shrink-0 text-n-blue-11 group-open:hidden"
                  aria-hidden="true"
                />
                <span
                  class="i-lucide-minus hidden size-4 shrink-0 text-n-blue-11 group-open:block"
                  aria-hidden="true"
                />
              </summary>
              <div class="px-3 pb-3 pt-1">
                <p class="text-xs text-n-slate-11">
                  {{ $t('CRM.followUpLimit') }}:
                  {{ stage?.follow_up_limit || 0 }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('CRM.followUpHelp') }}
                </p>
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-plus"
                  class="bg-n-solid-3"
                  :label="$t('CRM.recordAttempt')"
                  :disabled="!editable || state.busy"
                  @click="openDialog('follow_up')"
                />
              </div>
            </details>
          </template>
          <button
            v-else-if="funnel && data.can_edit"
            :disabled="state.busy"
            class="flex items-center justify-center gap-1 rounded-lg bg-n-brand p-3 text-white disabled:opacity-50"
            @click="execute('start')"
          >
            <template v-if="conversationClosed">
              <span>{{ $t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN') }}</span>
              <span
                class="i-lucide-arrow-right size-4 shrink-0"
                aria-hidden="true"
              />
            </template>
            <span>{{ $t('CRM.start') }}</span>
          </button>
        </template>
        <button
          v-if="showHistory"
          type="button"
          class="flex items-center gap-2 self-start rounded-lg px-2 py-2 text-n-blue-11 hover:bg-n-alpha-2"
          @click="showHistory = false"
        >
          <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
          {{ $t('CRM.backToService') }}
        </button>
        <details
          v-if="showHistory"
          open
          class="group rounded-lg border border-n-weak"
        >
          <summary
            class="flex min-h-11 cursor-pointer list-none items-center gap-2 px-3 py-2 font-medium [&::-webkit-details-marker]:hidden"
            :aria-label="$t('CRM.audit')"
          >
            <span
              class="i-lucide-history size-4 shrink-0 text-n-slate-11"
              aria-hidden="true"
            />
            <span class="flex-1">{{ $t('CRM.history') }}</span>
            <span
              class="i-lucide-plus size-4 shrink-0 text-n-blue-11 group-open:hidden"
              aria-hidden="true"
            />
            <span
              class="i-lucide-minus hidden size-4 shrink-0 text-n-blue-11 group-open:block"
              aria-hidden="true"
            />
          </summary>
          <div class="px-3 pb-3">
            <ol class="mt-3 flex list-none flex-col gap-4 p-0">
              <li
                v-for="event in data.events"
                :key="event.id"
                class="border-b border-n-weak pb-3"
              >
                <strong>{{ $t(`CRM.actions.${event.action}`) }}</strong>
                <p class="m-0 text-xs text-n-slate-11">
                  {{ event.user }} · {{ format(event.at) }}
                </p>
                <p v-if="event.details.reason" class="mb-0 break-words">
                  {{
                    event.details.reason ===
                    'Cotação classification replaces legacy quote labels'
                      ? $t('CRM.classifications.migrationReason')
                      : event.details.reason
                  }}
                </p>
                <p v-if="event.details.note" class="mb-0 break-words">
                  {{ event.details.note }}
                </p>
                <p
                  v-if="event.details.pending_requirement_names?.length"
                  role="alert"
                  class="mb-0 text-n-amber-11"
                >
                  {{ $t('CRM.automationPendingRequirements') }}:
                  {{ event.details.pending_requirement_names.join(', ') }}
                </p>
                <p
                  v-if="event.action === 'automation_pending'"
                  role="alert"
                  class="mb-0 text-n-amber-11"
                >
                  {{ $t('CRM.automationNeedsLink') }}
                </p>
                <ul
                  v-if="event.details.results"
                  class="my-2 list-none space-y-1 p-0 text-xs"
                >
                  <li
                    v-for="(result, index) in event.details.results"
                    :key="index"
                  >
                    <span v-if="result.id"
                      >{{ result.name || `#${result.id}` }} ·
                    </span>
                    {{ $t(`CRM.stageActions.results.${result.status}`) }}
                  </li>
                </ul>
                <details class="mt-2">
                  <summary class="cursor-pointer text-xs">
                    {{ $t('CRM.changes') }}
                  </summary>
                  <p class="break-words text-xs">
                    <strong>{{ $t('CRM.before') }}:</strong>
                    {{ auditSummary(event.before) }}
                  </p>
                  <p class="break-words text-xs">
                    <strong>{{ $t('CRM.after') }}:</strong>
                    {{ auditSummary(event.after) }}
                  </p>
                </details>
                <Button
                  v-if="editable && event.can_undo"
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-undo-2"
                  class="mt-2 bg-n-solid-3"
                  :label="$t('CRM.undo')"
                  :disabled="state.busy"
                  @click="
                    undoId = event.id;
                    openDialog('undo');
                  "
                />
              </li>
            </ol>
            <button
              v-if="data.more_events"
              :disabled="state.busy"
              class="mt-3 rounded border border-n-weak p-2"
              @click="loadOlder"
            >
              {{ $t('CRM.olderEvents') }}
            </button>
          </div>
        </details>
        <template v-if="!showHistory">
          <template v-if="active">
            <div
              v-if="editable"
              class="mt-3 flex flex-col gap-3 border-t border-n-weak pt-4"
            >
              <div class="min-w-0">
                <p class="m-0 text-xs text-n-slate-11">
                  {{ $t('CRM.nextStage') }}
                </p>
                <p
                  class="mb-0 mt-1 break-words text-base font-semibold text-n-slate-12"
                >
                  {{ nextStage?.name || $t('CRM.finish') }}
                </p>
              </div>
              <CrmNextStagePreview
                v-if="nextStage"
                :actions="nextStageActions"
              />
              <p
                v-if="!classificationsComplete"
                class="m-0 text-sm text-n-amber-11"
                role="alert"
              >
                {{ $t('CRM.classifications.requiredBeforeAdvance') }}
              </p>
              <div class="flex min-w-0 items-center gap-2">
                <Button
                  v-if="previousStages.length"
                  variant="outline"
                  color="slate"
                  size="lg"
                  icon="i-lucide-undo-2"
                  :aria-label="$t('CRM.return')"
                  :title="$t('CRM.return')"
                  :disabled="state.busy"
                  @click="openDialog('return')"
                />
                <Button
                  color="blue"
                  size="lg"
                  icon="i-lucide-arrow-right"
                  trailing-icon
                  class="min-w-0 flex-1"
                  :label="nextStage ? $t('CRM.advance') : $t('CRM.complete')"
                  :disabled="state.busy || !classificationsComplete"
                  @click="openDialog('advance')"
                />
              </div>
              <Button
                color="ruby"
                size="sm"
                icon="i-lucide-circle-stop"
                class="mt-2 self-start"
                :label="$t('CRM.close')"
                :disabled="state.busy"
                @click="openDialog('close')"
              />
            </div>
            <p v-else class="mt-3 text-xs text-n-slate-11">
              {{ $t('CRM.readOnly') }}
            </p>
          </template>
        </template>
      </template>
    </template>
    <dialog
      v-if="dialog"
      ref="modal"
      aria-modal="true"
      :aria-label="$t(`CRM.dialogs.${dialog}`)"
      class="max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-auto rounded-xl bg-n-solid-1 p-0 text-n-slate-12 backdrop:bg-black/40"
      @cancel.prevent="dialog = ''"
    >
      <form
        class="max-h-full w-full max-w-md overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-lg"
        @submit.prevent="submit"
      >
        <h3 class="text-base font-semibold">
          {{ $t(`CRM.dialogs.${dialog}`) }}
        </h3>
        <p
          v-if="dialog === 'advance' && nextStage"
          class="text-sm text-n-slate-11"
        >
          {{ $t('CRM.board.selectedStage', { stage: nextStage.name }) }}
        </p>
        <template v-if="dialog === 'advance'">
          <p v-if="completeCount < requirements.length">
            {{ $t('CRM.pendingWarning') }}
          </p>
          <ul>
            <li
              v-for="item in requirements.filter(r => !completed[r.id])"
              :key="item.id"
            >
              {{ item.name }}
            </li>
          </ul>
          <label
            v-if="
              completeCount < requirements.length && !data.skip_confirmation
            "
            class="flex gap-2"
            ><input v-model="confirmPending" type="checkbox" required />{{
              $t('CRM.confirmPending')
            }}</label
          >
          <label
            v-if="active.state.review_required"
            class="mt-3 flex items-start gap-2 text-sm text-n-slate-12"
            ><input v-model="confirmReview" type="checkbox" required />{{
              $t('CRM.confirmReview')
            }}</label
          >
        </template>
        <label
          v-if="dialog === 'extend'"
          class="block w-full text-sm font-medium text-n-slate-12"
          >{{ $t('CRM.businessMinutes')
          }}<input
            v-model.number="minutes"
            type="number"
            min="1"
            required
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-field p-2 text-sm text-n-slate-12"
        /></label>
        <div v-if="dialog === 'return'" class="mt-4 space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('CRM.stage') }}
          </label>
          <ComboBox
            v-model="target"
            :allow-deselect="false"
            :options="
              previousStages.map(item => ({ value: item.id, label: item.name }))
            "
            :aria-label="$t('CRM.stage')"
          />
        </div>
        <label
          v-if="dialog === 'close'"
          class="block w-full text-sm font-medium text-n-slate-12"
          >{{ $t('CRM.reason')
          }}<select
            v-model="reason"
            required
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-field p-2 text-sm text-n-slate-12"
          >
            <option
              v-for="item in [
                'completed',
                'no_response',
                'withdrawal',
                'service_failure',
                'other',
              ]"
              :key="item"
              :value="item"
            >
              {{ $t(`CRM.reasons.${item}`) }}
            </option>
          </select></label
        >
        <label
          v-if="['extend', 'return', 'undo'].includes(dialog)"
          class="mt-3 block w-full text-sm font-medium text-n-slate-12"
          >{{ $t('CRM.reason')
          }}<textarea
            v-model="reason"
            required
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-field p-2 text-sm text-n-slate-12"
          />
        </label>
        <label
          v-if="dialog === 'close'"
          class="mt-3 block w-full text-sm font-medium text-n-slate-12"
          >{{ $t('CRM.note')
          }}<textarea
            v-model="note"
            :required="reason === 'other'"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-field p-2 text-sm text-n-slate-12"
          />
        </label>
        <label
          v-if="dialog === 'follow_up'"
          class="block w-full text-sm font-medium text-n-slate-12"
          >{{ $t('CRM.sentMessageId')
          }}<select
            v-model="messageId"
            required
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-field p-2 text-sm text-n-slate-12"
          >
            <option
              v-for="message in data.sent_messages"
              :key="message.id"
              :value="message.id"
            >
              {{ format(message.at) }} · {{ message.content }}
            </option>
          </select></label
        >
        <p v-if="state.error" role="alert" class="text-n-ruby-11">
          {{ state.error }}
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-2 text-sm font-medium hover:bg-n-alpha-1"
            @click="dialog = ''"
          >
            {{ $t('CRM.cancel') }}
          </button>
          <button
            :disabled="state.busy || (dialog === 'return' && !target)"
            class="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            :class="
              dialog === 'close'
                ? 'bg-n-ruby-9 hover:bg-n-ruby-10'
                : 'bg-n-brand'
            "
          >
            {{ $t('CRM.confirm') }}
          </button>
        </div>
      </form>
    </dialog>
  </div>
</template>
